import { TitleLabel } from '@/components/_SiteWide/TitleLabel'
import { formatCurrency } from '@/utils/formatters'
import { BBDTag, GraviButton, Horizontal, MaterialIcon, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Card, Popconfirm, Tooltip } from 'antd'
import { isInvoiceVoided } from './util'
import type { MatchDetailPaneProps, MatchDetailRow } from './MatchDetailPane'
import type { MatchDetailResponse } from '@/api/useSupplierReconciliation/useMatchDetail'
import { formatters } from '@/utils/intl/dateTimeFormatters'
import { formatInvoiceDate } from '../../columnDefs/detailColumnDefs/utils/formatters'
import { useMatchActions } from '../../../hooks'

type InvoiceCardProps = {
  invoice: MatchDetailRow
  match: MatchDetailResponse
  hasBulkApprovePermission: boolean
  handleVoid: (invoiceId: string) => Promise<void>
  matchActions: MatchDetailPaneProps['matchActions']
  handleUnmatch: ((match: MatchDetailRow) => void) | null
}

export function InvoiceCard({
  invoice,
  match,
  hasBulkApprovePermission,
  handleVoid,
  matchActions,
  handleUnmatch,
}: InvoiceCardProps) {
  const canShowVoidActions = match?.status !== 'approved'
  const isMatchApproved = match?.status === 'approved'
  const isVoided = isInvoiceVoided(match, invoice.document_id)

  return (
    <Card
      className={`${isVoided ? 'ant-tag ant-tag-error' : ''}`}
      key={invoice.document_id}
      size='small'
      style={{
        borderRadius: 6,
      }}
      styles={{ body: { padding: 12 } }}
    >
      <Vertical gap={8}>
        <div>
          <Horizontal justifyContent='space-between' verticalCenter>
            <TitleLabel title='INV #' strikethrough={isVoided}>
              {invoice.document_number ?? invoice.invoice_number}
            </TitleLabel>
            <TitleLabel title='Total' align='right' strikethrough={isVoided}>
              {invoice.document_id?.startsWith('activity-') ? (
                <span style={{ color: 'var(--theme-text-secondary)' }}>Amount N/A</span>
              ) : (
                formatCurrency(invoice.total)
              )}
            </TitleLabel>
          </Horizontal>
          {invoice?.bol_number && <Texto>BOL #{invoice.bol_number}</Texto>}
        </div>

        <Horizontal justifyContent='space-between' verticalCenter>
          <TitleLabel title='Supplier' strikethrough={isVoided}>
            {invoice?.supplier?.internal_name ?? invoice.supplier?.source_name ?? 'Unknown Supplier'}
          </TitleLabel>
          <TitleLabel title='BOL Date' align='right' strikethrough={isVoided}>
            {formatInvoiceDate(
              invoice.invoice_date ?? invoice.invoice_date_local ?? invoice.document_date,
              invoice.timezone_data?.terminal
            )}
          </TitleLabel>
        </Horizontal>

        <Horizontal justifyContent='space-between' verticalCenter>
          <TitleLabel title='Invoice Created' strikethrough={isVoided}>
            {formatters.DATE(invoice.creation_date ?? '')}
          </TitleLabel>
          <TitleLabel title='Invoice Due' align='right' strikethrough={isVoided}>
            {formatters.DATE(invoice.due_date ?? invoice.due_date_utc ?? '', invoice.timezone_data?.terminal)}
          </TitleLabel>
        </Horizontal>
      </Vertical>

      <Horizontal className='pt-2' width='100%' justifyContent='space-between' verticalCenter>
        {handleUnmatch && (
          <Tooltip title='Unmatch'>
            <Popconfirm
              title='Unmatch'
              description='Are you sure you want to unmatch?'
              onConfirm={() => handleUnmatch(invoice)}
              okText='Yes, Unmatch'
              cancelText='Cancel'
            >
              <GraviButton size='small' icon={<MaterialIcon className='pt-1' icon='link_off' />} type='text'>
                Unmatch
              </GraviButton>
            </Popconfirm>
          </Tooltip>
        )}
        <div>{isVoided && <BBDTag error={true}>Voided</BBDTag>}</div>
        {/* Show void/restore button based on individual invoice status */}
        {canShowVoidActions ? (
          isVoided ? (
            // Show restore button for void invoices
            <Popconfirm
              title='Restore Invoice'
              description={`Are you sure you want to restore invoice ${invoice.invoice_number}?`}
              onConfirm={async () => {
                await matchActions.handleUnvoidInvoice(invoice.id)
              }}
              okText='Yes, Restore'
              cancelText='Cancel'
              disabled={isMatchApproved}
            >
              <Tooltip
                title={
                  !hasBulkApprovePermission
                    ? 'You do not have permission to restore invoices'
                    : isMatchApproved
                      ? 'Cannot restore invoice - match is approved'
                      : 'Restore Voided Invoice'
                }
              >
                <GraviButton
                  appearance='outlined'
                  size='small'
                  disabled={!hasBulkApprovePermission || isMatchApproved}
                  loading={matchActions.voidInvoiceMutation.isPending}
                  icon={<MaterialIcon icon='refresh' size={16} className='pt-1' />}
                >
                  Restore
                </GraviButton>
              </Tooltip>
            </Popconfirm>
          ) : (
            // Show void button for active invoices
            <Popconfirm
              title='Void Invoice'
              description={`Are you sure you want to void invoice ${invoice.document_number}?`}
              onConfirm={async () => {
                if (invoice.document_id) {
                  await handleVoid(invoice.document_id)
                }
              }}
              okText='Yes, Void'
              cancelText='Cancel'
              disabled={isMatchApproved}
            >
              <Tooltip
                title={
                  !hasBulkApprovePermission
                    ? 'You do not have permission to void invoices'
                    : isMatchApproved
                      ? 'Cannot void invoice - match is approved'
                      : 'Void Invoice'
                }
              >
                <GraviButton
                  appearance='outlined'
                  size='small'
                  disabled={!hasBulkApprovePermission || isMatchApproved}
                  loading={matchActions.voidInvoiceMutation.isPending}
                  icon={<MaterialIcon icon='cancel' size={16} className='pt-1' />}
                >
                  Void
                </GraviButton>
              </Tooltip>
            </Popconfirm>
          )
        ) : null}
      </Horizontal>
    </Card>
  )
}
